import { useEffect, useState } from "react";
import { logger } from "@/lib/logger";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { ArrowRight, Camera, Coins, ShieldCheck, ScanLine, Sparkles, HardHat, Heart } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import PartOfWorkSafeAgent from "@/components/PartOfWorkSafeAgent";
import { SafetyTeams } from "@/components/LessonsSections";
import { api } from "@/lib/api";

const HERO_IMG =
  "https://images.pexels.com/photos/33914927/pexels-photo-33914927.jpeg";

const STEPS = [
  {
    icon: Camera,
    step: "01",
    title: "Snap a photo",
    desc: "Your family. Your team. The reason you came home last night. Anything that makes you choose safe.",
  },
  {
    icon: ShieldCheck,
    step: "02",
    title: "Share your why",
    desc: "Write a sentence or a paragraph. We post approved stories on the Wall of Safety.",
  },
  {
    icon: Coins,
    step: "03",
    title: "Earn a token",
    desc: "Receive a unique Safety Token redeemable at the company store. One submission, one token.",
  },
];

const MARQUEE_REPEAT = [
  "marquee-a",
  "marquee-b",
];

const MARQUEE_ITEMS = [
  "m-1",
  "m-2",
  "m-3",
  "m-4",
  "m-5",
  "m-6",
  "m-7",
  "m-8",
];

export default function Landing() {
  const publicBase = process.env.REACT_APP_PUBLIC_URL || window.location.origin;
  const submitUrl = `${publicBase}/submit`;
  const [wall, setWall] = useState([]);
  const [highlights, setHighlights] = useState({ items: [], fallback: false, window_days: 7 });

  useEffect(() => {
    api
      .get("/submissions/wall")
      .then((r) => setWall(r.data || []))
      .catch((err) => {
        logger.warn("[landing] wall load failed", err);
        setWall([]);
      });
    api
      .get("/submissions/wall/highlights?days=7&limit=3")
      .then((r) => setHighlights(r.data || { items: [], fallback: false }))
      .catch((err) => logger.warn("[landing] highlights load failed", err));
  }, []);

  return (
    <div className="bg-white text-[#09090B]">
      <SiteHeader />

      {/* Hero */}
      <section className="relative overflow-hidden border-b-2 border-[#09090B]">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Worker on scaffolding"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#09090B]/95 via-[#09090B]/85 to-[#09090B]/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 text-white ws-fade-up">
            <div className="inline-flex items-center gap-2 bg-[#FF5A00] text-white px-4 py-2 ws-label border-2 border-white">
              <Sparkles strokeWidth={2.5} className="w-4 h-4" /> worksafestory.com · Why I Work Safe
            </div>
            <h1
              data-testid="hero-headline"
              className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] mt-6"
            >
              Why I work <span className="text-[#FFCC00]">safe.</span>
            </h1>
            <p data-testid="hero-message" className="mt-6 text-lg text-[#E4E4E7] max-w-xl leading-relaxed">
              Share the reason you go home safe. Stories and near misses, reviewed by a person,
              become lessons and toolbox talks for the next crew.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/submit"
                data-testid="hero-submit-btn"
                className="inline-flex items-center gap-3 bg-[#FF5A00] hover:bg-[#E65100] text-white font-bold px-8 py-4 ws-label border-2 border-white ws-hard-shadow"
              >
                Share my story <ArrowRight strokeWidth={2.5} className="w-5 h-5" />
              </Link>
              <Link
                to="/wall"
                data-testid="hero-wall-btn"
                className="inline-flex items-center gap-3 bg-transparent border-2 border-white text-white font-bold px-8 py-4 ws-label hover:bg-white hover:text-[#09090B] transition-colors"
              >
                Visit the Wall
              </Link>
            </div>
            <a
              href="#safety-teams"
              data-testid="hero-safety-teams-link"
              className="mt-6 inline-block text-sm font-bold text-white border-b-2 border-[#FFCC00] pb-0.5 hover:text-[#FFCC00] transition-colors"
            >
              For safety teams →
            </a>
          </div>

          {/* QR card */}
          <div className="lg:col-span-5 ws-fade-up">
            <div className="bg-white p-8 border-2 border-[#09090B] ws-hard-shadow">
              <div className="flex items-center justify-between">
                <div className="ws-label">Scan to submit</div>
                <ScanLine strokeWidth={2.5} className="w-5 h-5 text-[#FF5A00]" />
              </div>
              <div className="mt-6 flex items-center justify-center bg-[#F4F4F5] border-2 border-[#09090B] p-6">
                <QRCodeSVG
                  data-testid="qr-code"
                  value={submitUrl}
                  size={220}
                  level="M"
                  bgColor="#F4F4F5"
                  fgColor="#09090B"
                  includeMargin={false}
                />
              </div>
              <div className="mt-6 border-t-2 border-dashed border-[#09090B] pt-4">
                <div className="ws-label text-[#52525B]">Or open in browser</div>
                <div className="font-mono text-sm text-[#09090B] truncate mt-1">
                  {submitUrl}
                </div>
              </div>
              <div className="mt-4 text-xs text-[#52525B] leading-relaxed">
                Print this poster for the breakroom, lockers, and crew vans. Every scan is one
                more shift, one more story, one more reason to stay sharp.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Crew stories */}
      <section className="border-b-2 border-[#09090B]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
          <div className="flex items-end justify-between flex-wrap gap-6">
            <div>
              <div className="ws-label text-[#FF5A00]">/ 01 — Share your why</div>
              <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-3 max-w-2xl">
                Three steps. One story. A token in your pocket.
              </h2>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {STEPS.map((s, i) => (
              <div
                key={s.step}
                className="bg-white border-2 border-[#09090B] p-8 ws-hard-shadow"
                data-testid={`step-${i + 1}`}
              >
                <div className="flex items-start justify-between">
                  <div className="w-12 h-12 bg-[#09090B] text-white flex items-center justify-center">
                    <s.icon strokeWidth={2.5} className="w-6 h-6" />
                  </div>
                  <div className="font-display font-black text-5xl text-[#E4E4E7] tracking-tighter">
                    {s.step}
                  </div>
                </div>
                <h3 className="font-display font-bold text-2xl mt-6 tracking-tight">{s.title}</h3>
                <p className="text-[#52525B] mt-3 leading-relaxed text-base">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Wall preview */}
      <section className="border-b-2 border-[#09090B]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
          <div className="flex items-end justify-between flex-wrap gap-6">
            <div>
              <div className="ws-label text-[#FF5A00]">
                {highlights.fallback ? "/ 02 — Wall of Safety" : "/ 02 — Trending this week"}
              </div>
              <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-3 max-w-2xl">
                {highlights.fallback ? "Stories from the floor." : "What the crew is loving right now."}
              </h2>
            </div>
            <Link
              to="/wall"
              data-testid="wall-cta"
              className="ws-label border-b-2 border-[#09090B] pb-1 hover:text-[#FF5A00] hover:border-[#FF5A00] transition-colors"
            >
              See all stories →
            </Link>
          </div>

          {highlights.items.length === 0 && (
            <div className="mt-12 border-2 border-dashed border-[#09090B] p-12 text-center" data-testid="landing-wall-empty">
              <h3 className="font-display font-bold text-2xl">Be the first to share a safety story</h3>
              <Link
                to="/submit"
                data-testid="landing-wall-empty-cta"
                className="inline-flex items-center gap-2 mt-6 bg-[#FF5A00] text-white font-bold px-6 py-3 text-xs uppercase tracking-[0.2em] border-2 border-[#09090B] ws-hard-shadow"
              >
                Share my story →
              </Link>
            </div>
          )}
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {highlights.items.map((item, i) => {
              const photo = item.photo_url;
              const name = item.employee_name;
              const quote = item.reason;
              const key = item.id;
              const applause = item.applause;
              const showApplause = applause > 0 && !highlights.fallback;
              return (
                <article
                  key={key}
                  className="bg-white border-2 border-[#09090B] overflow-hidden ws-hard-shadow"
                  data-testid={`wall-card-${i}`}
                >
                  <div className="aspect-[4/5] overflow-hidden border-b-2 border-[#09090B] bg-[#F4F4F5]">
                    <img src={photo} alt={name} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="ws-label text-[#FF5A00]">Reason · 0{i + 1}</div>
                      {showApplause && (
                        <div
                          className="inline-flex items-center gap-1.5 bg-[#FFCC00] border-2 border-[#09090B] px-2.5 py-0.5"
                          data-testid={`trending-applause-${i}`}
                        >
                          <Heart strokeWidth={2.5} className="w-3.5 h-3.5 fill-[#09090B]" />
                          <span className="font-mono font-bold text-[#09090B] text-sm">{applause}</span>
                        </div>
                      )}
                    </div>
                    <blockquote className="font-display font-bold text-xl tracking-tight mt-2 leading-snug">
                      “{quote}”
                    </blockquote>
                    <div className="mt-4 flex items-center gap-2 text-sm text-[#52525B]">
                      <HardHat strokeWidth={2.5} className="w-4 h-4" />
                      <span>{name}</span>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Reward Strip */}
      <section className="bg-[#FFCC00] border-b-2 border-[#09090B] overflow-hidden">
        <div className="ws-marquee py-4">
          {MARQUEE_REPEAT.map((rk) => (
            <div key={rk} className="flex items-center gap-8 px-4 shrink-0">
              {MARQUEE_ITEMS.map((ik) => (
                <span
                  key={`${rk}-${ik}`}
                  className="font-display font-black text-2xl tracking-tighter uppercase flex items-center gap-4"
                >
                  ★ One Story = One Safety Token ★ Redeem at the Company Store
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      <SafetyTeams />

      <PartOfWorkSafeAgent />

      <SiteFooter />
    </div>
  );
}
